import { config } from 'app/config'
import { isError, isObject, values } from 'lodash-es'
import { nanoid } from 'nanoid'
import io, { Socket } from 'socket.io-client'
import { CustomErrorWithCause } from 'utils/error'
import { logError, logWarn } from 'utils/logger'

export interface IDomainEvent {
  eventType: string
  callback: (args) => void
}
interface ISocketServiceParams {
  shipmentId?: string
  customerId?: string
  userId?: string
  app: string
}
interface ISocketServiceProps {
  socketUrl: string
  nameSpace: string
  params: ISocketServiceParams
}

interface ISocketService {
  createDomainEventsListeners: (domainEventMap: IDomainEvent[]) => void
  removeDomainEventsListeners: (domainEventMap: IDomainEvent[]) => void
  disconnect: () => void
}

interface ISocketParams {
  name: TScoketNames
  namespace: TSocketNameSpaces
}

export const SOCKET_NAME_REPORTS = 'reports-socket'
const SOCKET_NAME_SPACE_REPORTS = '/customer.report-updates'
type TSocketNameReports = typeof SOCKET_NAME_REPORTS
type TSocketNameSpaceReports = typeof SOCKET_NAME_SPACE_REPORTS
const reportsSocketParams: ISocketParams = {
  name: SOCKET_NAME_REPORTS,
  namespace: SOCKET_NAME_SPACE_REPORTS,
}

export const SOCKET_NAME_TASKS = 'tasks-socket'
export const SOCKET_NAME_SPACE_TASKS = '/shipment.tasks-updates'
type TSocketNameTasks = typeof SOCKET_NAME_TASKS
type TSocketNameSpaceTasks = typeof SOCKET_NAME_SPACE_TASKS

type TScoketNames = TSocketNameReports | TSocketNameTasks
type TSocketNameSpaces = TSocketNameSpaceReports | TSocketNameSpaceTasks
const socketParams: ISocketParams[] = [reportsSocketParams]

class SocketService {
  socket: Socket
  constructor(token: string, props: ISocketServiceProps) {
    const pathBasedRouting = props.socketUrl.startsWith('/')
    const socketOrigin = pathBasedRouting ? '' : props.socketUrl
    const socketPath = pathBasedRouting ? props.socketUrl : ''
    this.socket = io(socketOrigin + props.nameSpace, {
      path: `${socketPath}/socket.io/`,
      withCredentials: true,
      query: {
        ...props.params,
        sticky: nanoid(9),
      },
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
      forceNew: true,
    })
  }

  createDomainEventsListeners(domainEventsMap: { eventType: string; callback: Function }[]) {
    domainEventsMap.forEach((domainEvent) => {
      if (!this.socket.hasListeners(domainEvent.eventType)) {
        this.socket.on(domainEvent.eventType, (data) => domainEvent.callback(data))
      }
    })
    if (!this.socket.hasListeners('error')) {
      this.socket.on('error', (error) => {
        this.logSocketError('socket.io error event', error)
      })
    }
    if (!this.socket.hasListeners('connect_error')) {
      this.socket.on('connect_error', (error) => {
        this.logSocketError('socket.io connect_error event', error)
      })
    }
  }

  /**
   * Whenever an Error object is emitted, socket.io automatically reconnects and recovers from the
   * error, so we just log a warning because there's nothing we need to do. Also logging differently in the
   * case that the emitted error is not an error object, since that doesn't show the message in the same way.
   *
   * TODO: Check if these warn logs are useful. If not, remove them completely.
   */
  private logSocketError(message: string, error: unknown) {
    if (isError(error)) {
      logWarn(new CustomErrorWithCause(message, { cause: error }).toString())
    } else {
      // Stringifying in case it's an object, so that logs don't contain [object Object]
      const stringifiedError = isObject(error) ? JSON.stringify(error) : error
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
      logWarn(`${message}: ${stringifiedError}`)
    }
  }

  removeDomainEventsListeners(domainEventsMap: IDomainEvent[]) {
    domainEventsMap.forEach((domainEvent) => {
      this.socket.removeListener(domainEvent.eventType, domainEvent.callback)
    })
  }

  disconnect() {
    this.socket.disconnect()
  }
}

export class SocketManager {
  sockets: { [key: string]: ISocketService } = {}

  initSocket = (customerId, socketUserToken, app) => {
    socketParams.forEach((socketParam) => {
      this.createSocket(socketParam.name, socketUserToken, {
        socketUrl: config.WEBSOCKET_ENDPOINT,
        nameSpace: socketParam.namespace,
        params: {
          customerId,
          app,
        },
      })
    })
  }

  shouldLoadSocket() {
    if (config.WEBSOCKET_ENDPOINT === 'undefined') {
      logError(new Error('Please set WEBSOCKET_ENDPOINT env variable'))
      return false
    }
    return true
  }

  createSocket(socketId: string, token: string, props: ISocketServiceProps) {
    this.sockets[socketId] = new SocketService(token, props)
    return this.sockets[socketId]
  }

  get(id: string) {
    return this.sockets[id]
  }

  disconnect(id: string) {
    this.sockets[id].disconnect()
  }
  disconnectAll() {
    values(this.sockets).forEach((socket) => socket.disconnect())
  }
}
